import type { CacheConfig, Variables } from 'relay-runtime';
import isEqual from 'lodash/isEqual';

import { isDataFetcherAllFinished } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { SSR_TEST_QUERY_FIRED } from '@confluence/graphql/entry-points/SSRTestLink.ts';

// Keep query listeners at loading state when GLOBAL_APOLLO_SKIP_QUERY is true.
// SSR runtime will cancel all the in-flight requests once the main function exits.
// We allow queries that were already in-flight, and preloads during streaming.

const inFlightQueries = new Map<string, Array<Variables> | undefined>();

const clearInFlightQueries = () => {
	inFlightQueries.clear();
};

const isQueryInFlightWithVariables = (operationName: string, vars: Variables) =>
	Boolean(inFlightQueries.get(operationName)?.some((v) => isEqual(v, vars)));

const isPreload = (metadata: CacheConfig['metadata']): boolean => {
	// TODO: Migrate usage of `preload` from Apollo context to Relay's cacheConfig.metadata.
	// See Jira: CCE2EPERF-834 (created from CCE2EPERF-659 PR discussion)
	return Boolean(window.__SSR_REACT_STREAMING__ && metadata?.preload);
};

export const trackInFlightIfAllowed = ({
	operationName,
	variables,
}: {
	operationName: string;
	variables: Variables;
}) => {
	// TODO: Create GLOBAL_RELAY_SKIP_QUERY env var
	if (window['GLOBAL_APOLLO_SKIP_QUERY']) {
		return;
	}
	if (isQueryInFlightWithVariables(operationName, variables)) {
		return;
	}
	inFlightQueries.set(operationName, (inFlightQueries.get(operationName) || []).concat(variables));
};

export const shouldBlockRequest = ({
	operationName,
	variables,
	metadata,
	operationKind,
}: {
	operationName: string;
	variables: Variables;
	metadata: CacheConfig['metadata'];
	operationKind?: string;
}): boolean => {
	if (!window['GLOBAL_APOLLO_SKIP_QUERY']) {
		return false;
	}

	// experimental is not being used for Relay

	if (operationKind === 'subscription') {
		return false;
	}

	if (
		isQueryInFlightWithVariables(operationName, variables) ||
		isPreload(metadata) ||
		!isDataFetcherAllFinished()
	) {
		return false;
	}

	return true;
};

export const markSSRTestQueryFired = (operationName: string) => {
	SSR_TEST_QUERY_FIRED[operationName] = (SSR_TEST_QUERY_FIRED[operationName] || 0) + 1;
};

export const triggerWarnBlockedQuery = (operationName: string) => {
	if (
		process.env.CLOUD_ENV !== 'dev' &&
		process.env.CLOUD_ENV !== 'branch' &&
		process.env.CLOUD_ENV !== 'staging' &&
		process.env.CLOUD_ENV !== 'hello'
	) {
		return;
	}
	// eslint-disable-next-line no-console
	console.warn(`Query blocked after react rendering: ${operationName}`);
};

export const __TEST_ONLY__ = {
	_inFlightQueries: inFlightQueries,
	clearInFlightQueries,
};
